import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState
} from "react";

interface UseModalManager {
  isTopModal: (modalId: string) => boolean;
  addModal: (modalId: string) => void;
  removeModal: (modalId: string) => void;
  modalsOpen: boolean;
}

const ModalContext = createContext<UseModalManager | undefined>(undefined);

export const useModalManager = (): UseModalManager => {
  const value = useContext(ModalContext);
  if (!value) {
    throw new Error("Missing provider!");
  }
  return value;
};

const ModalManagerProvider = ({ children }: { children: ReactNode }) => {
  const [modalIds, setModalIds] = useState<string[]>([]);
  const addModal = useCallback((modalId: string) => {
    setModalIds(prevModalIds => [...prevModalIds, modalId]);
  }, []);
  const removeModal = useCallback(modalId => {
    setModalIds(prevModals => prevModals.filter(modal => modal !== modalId));
  }, []);
  const isTopModal = useCallback(
    modalId => {
      if (!modalId) {
        return false;
      }
      // Not a PageModal (i.e., Events modal or otherwise).
      // Treat as top modal.
      if (!modalIds.length) {
        return true;
      }
      return (
        modalIds.findIndex(modal => modal === modalId) === modalIds.length - 1
      );
    },
    [modalIds]
  );
  const modalsOpen = !!modalIds.length;
  const value = {
    isTopModal,
    addModal,
    removeModal,
    modalsOpen
  };
  return (
    <ModalContext.Provider value={value}>{children}</ModalContext.Provider>
  );
};

export default ModalManagerProvider;
